/**
 * Display-only removal of the curriculum prefix that level and class names carry
 * ("FBISE Primary", "FBISE Class 1-A", "BISE Punjab Class 2").
 *
 * The prefix stays in the stored names on purpose: the same class name under two boards must
 * not collide on the server, where students and subjects resolve a class by bare name. So
 * `manualData`, lesson-planning keys and every payload keep the full name; only what the user
 * READS goes through `stripCurriculumPrefix`. Never write a stripped name back into state or
 * send it to the API — filters, edits and saves keep the stored name.
 *
 * Used by the onboarding wizard and by the institute pages (through `buildSchoolCurricula`).
 * node-api mirrors the same rules in components/onboarding/curriculumDisplayNames.js.
 */

const trim = (value: unknown): string => String(value ?? '').trim()
const lower = (value: unknown): string => trim(value).toLowerCase()

/** The bracketed short form of a curriculum name ("Federal Board (FBISE)" → "FBISE"), or "". */
export const bracketedCurriculumCode = (curriculum: unknown): string =>
  trim(/\(([^()]+)\)\s*$/.exec(trim(curriculum))?.[1])

/**
 * The longest run of leading words every class name shares, leaving each name at least one word
 * of its own, cut back until it is part of the curriculum's own name ("FBISE" from
 * "FBISE Pre-primary 1" / "FBISE Pre-primary 2"; "BISE Punjab" from "BISE Punjab Class 1" /
 * "BISE Punjab Class 2"). "Grade" / "Class" / "Year" are shared too but name the ladder, not the
 * board, so they never count. "" when the names share no such prefix.
 */
export const sharedCurriculumPrefix = (curriculum: unknown, classNames: readonly unknown[]) => {
  const words = classNames
    .map(trim)
    .filter(Boolean)
    .map((name) => name.split(/\s+/))
  if (words.length === 0) return ''
  const shortest = Math.min(...words.map((w) => w.length))
  const shared: string[] = []
  for (let i = 0; i < shortest - 1; i += 1) {
    const word = words[0][i]
    if (!words.every((w) => lower(w[i]) === lower(word))) break
    shared.push(word)
  }
  const curriculumLower = lower(curriculum)
  while (shared.length > 0 && !curriculumLower.includes(lower(shared.join(' ')))) shared.pop()
  return shared.join(' ')
}

/**
 * Prefixes a name under `curriculum` may start with, longest first: the curriculum's short code
 * (what the wizard writes — "FBISE", "BISE KP"), the one derived from the curriculum's own class
 * names (the same rule the level drawer uses when it adds a class), the bracketed short form of
 * the curriculum ("Federal Board (FBISE)" → "FBISE") and the curriculum name itself.
 */
export const curriculumPrefixes = (
  curriculum: unknown,
  classNames: readonly unknown[] = [],
  shortCode?: unknown
): string[] => {
  const name = trim(curriculum)
  if (!name) return []
  const candidates = [
    trim(shortCode),
    sharedCurriculumPrefix(name, classNames),
    bracketedCurriculumCode(name),
    name,
  ].filter(Boolean)
  const unique = new Map<string, string>()
  candidates.forEach((c) => {
    if (!unique.has(lower(c))) unique.set(lower(c), c)
  })
  return Array.from(unique.values()).sort((a, b) => b.length - a.length)
}

/**
 * `name` without a leading prefix from `prefixes` (case-insensitive, followed by a space).
 * A name that is only the prefix, or does not start with one, comes back unchanged.
 */
export const stripCurriculumPrefix = (name: unknown, prefixes: readonly string[]): string => {
  const full = trim(name)
  const fullLower = full.toLowerCase()
  for (const prefix of prefixes) {
    const p = lower(prefix)
    if (p && fullLower.startsWith(`${p} `)) {
      const rest = full.slice(p.length).trimStart()
      if (rest) return rest
    }
  }
  return full
}

export type CurriculumPrefixIndex = {
  /** Prefixes per lower-cased curriculum name. */
  byCurriculum: Map<string, string[]>
  /** Every known prefix, longest first — for a name whose curriculum is not at hand. */
  all: string[]
}

/**
 * Build the prefix lookup once from rows that carry `name` (and `grade`) plus `curriculum` and,
 * when known, the curriculum's `shortCode`.
 */
export const buildCurriculumPrefixIndex = (
  rows: ReadonlyArray<{name?: unknown; grade?: unknown; curriculum?: unknown; shortCode?: unknown}>,
  extraCurricula: readonly unknown[] = []
): CurriculumPrefixIndex => {
  const namesByCurriculum = new Map<
    string,
    {curriculum: string; shortCode: string; names: string[]}
  >()
  const add = (curriculum: unknown, shortCode: unknown, ...names: unknown[]) => {
    const c = trim(curriculum)
    if (!c) return
    const entry = namesByCurriculum.get(c.toLowerCase()) ?? {
      curriculum: c,
      shortCode: '',
      names: [],
    }
    if (!entry.shortCode) entry.shortCode = trim(shortCode)
    names.forEach((n) => {
      const t = trim(n)
      if (t) entry.names.push(t)
    })
    namesByCurriculum.set(c.toLowerCase(), entry)
  }
  rows.forEach((row) => add(row?.curriculum, row?.shortCode, row?.name))
  extraCurricula.forEach((c) => add(c, ''))
  const byCurriculum = new Map<string, string[]>()
  namesByCurriculum.forEach(({curriculum, shortCode, names}, key) => {
    byCurriculum.set(key, curriculumPrefixes(curriculum, names, shortCode))
  })
  const all = Array.from(new Set(Array.from(byCurriculum.values()).flat())).sort(
    (a, b) => b.length - a.length
  )
  return {byCurriculum, all}
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Full stored name → display name for every class and level in `rows` whose display form
 * differs, longest full name first. Feeds `stripCurriculumPrefixesInText`.
 */
export const buildDisplayNamePairs = (
  index: CurriculumPrefixIndex,
  rows: ReadonlyArray<{name?: unknown; level?: unknown; curriculum?: unknown}>
): Array<[string, string]> => {
  const pairs = new Map<string, [string, string]>()
  const add = (full: unknown, curriculum: unknown) => {
    const name = trim(full)
    if (!name || pairs.has(name.toLowerCase())) return
    const display = displayCurriculumName(index, name, curriculum)
    if (display !== name) pairs.set(name.toLowerCase(), [name, display])
  }
  rows.forEach((row) => {
    add(row?.name, row?.curriculum)
    add(row?.level, row?.curriculum)
  })
  return Array.from(pairs.values()).sort((a, b) => b[0].length - a[0].length)
}

/**
 * Free text (AI chat turns, server warnings) with every known prefixed class/level name
 * replaced by its display form. Only whole names from `pairs` are touched — never a bare
 * curriculum word — so unrelated text is left exactly as written.
 */
export const stripCurriculumPrefixesInText = (
  text: string,
  pairs: ReadonlyArray<[string, string]>
): string => {
  if (!text || pairs.length === 0) return text
  const byLower = new Map(pairs.map(([full, display]) => [full.toLowerCase(), display]))
  const pattern = new RegExp(
    `(?<![\\p{L}\\p{N}])(${pairs.map(([full]) => escapeRegExp(full)).join('|')})(?![\\p{L}\\p{N}])`,
    'giu'
  )
  return text.replace(pattern, (match) => byLower.get(match.toLowerCase()) ?? match)
}

/**
 * Display text typed or picked by the user → the stored name it stands for: a full stored
 * name is kept, a display name that belongs to exactly one stored name maps back to it, and
 * anything else (a new or unmatched value) is returned as typed.
 */
export const resolveDisplayedName = (
  text: unknown,
  pairs: ReadonlyArray<[string, string]>,
  storedNames: readonly unknown[] = []
): string => {
  const typed = trim(text)
  if (!typed) return typed
  const typedLower = typed.toLowerCase()
  const exact = storedNames.map(trim).find((n) => n.toLowerCase() === typedLower)
  if (exact) return exact
  const matches = Array.from(
    new Set(pairs.filter(([, display]) => display.toLowerCase() === typedLower).map(([f]) => f))
  )
  return matches.length === 1 ? matches[0] : typed
}

/**
 * Display form of a level or class name. With the curriculum known only its prefixes apply;
 * without it every known prefix is tried (still only ever at the very start of the name).
 */
export const displayCurriculumName = (
  index: CurriculumPrefixIndex,
  name: unknown,
  curriculum?: unknown
): string => {
  const prefixes = trim(curriculum)
    ? (index.byCurriculum.get(lower(curriculum)) ?? curriculumPrefixes(curriculum))
    : index.all
  return stripCurriculumPrefix(name, prefixes)
}

/** A level as the institute endpoints return it (`SELECT l.*` on `levels`). */
export type LevelCurriculumSource = {
  name?: unknown
  curriculum_name?: unknown
  curriculum_short_code?: unknown
}

/**
 * What the institute pages need to show a stored level, grade or class name: which curricula the
 * school has, the prefixes each one's names open with, and the short label shown beside a name.
 * Build it once per levels list with `buildSchoolCurricula`.
 */
export type SchoolCurricula = {
  index: CurriculumPrefixIndex
  /** Short label per lower-cased curriculum name ("FBISE", "BISE KP"). */
  labels: Map<string, string>
  /** Every known prefix, longest first, with the curriculum (lower-cased) it belongs to. */
  prefixOwners: Array<{prefix: string; curriculum: string}>
  /** The levels span two or more curricula, so a name needs its curriculum beside it. */
  multiple: boolean
}

/** Build the school's curricula from its levels. Levels without a curriculum are ignored. */
export const buildSchoolCurricula = (
  levels: ReadonlyArray<LevelCurriculumSource> | null | undefined
): SchoolCurricula => {
  const rows = (levels ?? [])
    .filter((level) => trim(level?.curriculum_name))
    .map((level) => ({
      name: level.name,
      curriculum: level.curriculum_name,
      shortCode: level.curriculum_short_code,
    }))
  const index = buildCurriculumPrefixIndex(rows)
  const labels = new Map<string, string>()
  rows.forEach((row) => {
    const key = lower(row.curriculum)
    if (labels.has(key)) return
    labels.set(
      key,
      trim(row.shortCode) || bracketedCurriculumCode(row.curriculum) || trim(row.curriculum)
    )
  })
  const prefixOwners = Array.from(index.byCurriculum.entries())
    .flatMap(([curriculum, prefixes]) => prefixes.map((prefix) => ({prefix, curriculum})))
    .sort((a, b) => b.prefix.length - a.prefix.length)
  return {index, labels, prefixOwners, multiple: labels.size > 1}
}

/**
 * The curriculum (lower-cased name) the first of `names` that opens with a known prefix belongs
 * to, or null. Pass a row's other names too ("Grade 1" has no prefix, its level "FBISE Primary"
 * does) so a name without a prefix still gets its curriculum label.
 */
export const detectCurriculum = (school: SchoolCurricula, ...names: unknown[]): string | null => {
  for (const name of names) {
    const value = lower(name)
    if (!value) continue
    const owner = school.prefixOwners.find(({prefix}) => value.startsWith(`${lower(prefix)} `))
    if (owner) return owner.curriculum
  }
  return null
}

/** A stored name split for display: the bare name and the curriculum label to show beside it. */
export type CurriculumNameParts = {
  name: string
  /** Shown only when the school has more than one curriculum; null otherwise or when unknown. */
  curriculum: string | null
}

/**
 * Split a stored level, grade or class name for display. The curriculum is `curriculum` when the
 * caller has it (the level's `curriculum_name`), else the one whose prefix the name opens with.
 * A trailing " - FBISE" the API adds to level labels ("FBISE Primary - FBISE (Main Branch)") is
 * dropped as well, since the curriculum is shown on its own.
 */
export const splitCurriculumName = (
  school: SchoolCurricula,
  name: unknown,
  curriculum?: unknown
): CurriculumNameParts => {
  const full = trim(name)
  if (!full || school.labels.size === 0) return {name: full, curriculum: null}
  const key = school.index.byCurriculum.has(lower(curriculum))
    ? lower(curriculum)
    : (detectCurriculum(school, full) ?? '')
  if (!key) return {name: full, curriculum: null}
  const prefixes = school.index.byCurriculum.get(key) ?? []
  let display = stripCurriculumPrefix(full, prefixes)
  for (const prefix of prefixes) {
    const suffix = new RegExp(`\\s+-\\s+${escapeRegExp(prefix)}(?=\\s*(\\(|$))`, 'i')
    if (suffix.test(display)) {
      display = display.replace(suffix, '')
      break
    }
  }
  return {name: display, curriculum: school.multiple ? (school.labels.get(key) ?? null) : null}
}

/**
 * A stored level, grade or class name as plain display text: "Grade 1-B · FBISE" when the school
 * has several curricula, "Grade 1-B" when it has one. For option labels and other text-only spots.
 */
export const formatCurriculumName = (
  school: SchoolCurricula,
  name: unknown,
  curriculum?: unknown
): string => {
  const parts = splitCurriculumName(school, name, curriculum)
  return parts.curriculum ? `${parts.name} · ${parts.curriculum}` : parts.name
}

/**
 * An option label built from a stored name and a stored detail in brackets (usually its level):
 * "Grade 1 (Primary (Main Branch)) · FBISE". Both lose their prefix; the curriculum, taken from
 * either, is shown once at the end and only when the school has several.
 */
export const formatCurriculumOption = (
  school: SchoolCurricula,
  name: unknown,
  detail?: unknown,
  curriculum?: unknown
): string => {
  const key = school.index.byCurriculum.has(lower(curriculum))
    ? lower(curriculum)
    : detectCurriculum(school, name, detail)
  const main = splitCurriculumName(school, name, key)
  const sub = trim(detail) ? splitCurriculumName(school, detail, key) : null
  const label = sub?.name ? `${main.name} (${sub.name})` : main.name
  const tag = main.curriculum ?? sub?.curriculum
  return tag ? `${label} · ${tag}` : label
}

/**
 * A stored level, grade or class name with its curriculum prefix (and the API's " - FBISE"
 * suffix) removed and no curriculum label added, even in a school with several curricula. For
 * the Level & Branch cells, where the name column beside them already shows the curriculum.
 */
export const bareCurriculumName = (
  school: SchoolCurricula,
  name: unknown,
  curriculum?: unknown
): string => splitCurriculumName(school, name, curriculum).name
